import { ArrowRight, TrendingUp, Users, Scale, HeartPulse, Megaphone, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const HERO_IMG = "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1400&q=80";

const practices = [
  {
    icon: TrendingUp, color: "var(--accent-mint)",
    title: "Finance & Accounting",
    body: "Interim and permanent leaders through month-end close, audit prep, and scaling cycles.",
    roles: ["CFOs & controllers (interim)", "FP&A analysts", "Payroll & benefits admins", "CPA-designated accountants"],
  },
  {
    icon: Users, color: "var(--accent-teal)",
    title: "HR & People Ops",
    body: "The operators who turn headcount plans into hires — and make culture stick.",
    roles: ["CHRO / VP HR (interim)", "HR business partners", "Talent acquisition leads", "Compensation analysts"],
  },
  {
    icon: Scale, color: "var(--accent-purple)",
    title: "Legal & Compliance",
    body: "Canadian counsel for privacy, contracts, and regulatory mandates — including Law 25 and PIPEDA.",
    roles: ["In-house counsel", "Privacy officers (PIPEDA)", "Contract managers", "Regulatory affairs"],
  },
  {
    icon: HeartPulse, color: "var(--accent-coral)",
    title: "Healthcare & Life Sciences",
    body: "Specialized roles across biotech, medical devices, and clinical operations — Health Canada fluent.",
    roles: ["Clinical data managers", "Regulatory (Health Canada)", "Biotech lab technicians", "Medical affairs specialists"],
  },
  {
    icon: Megaphone, color: "var(--accent-purple)",
    title: "Sales & Marketing",
    body: "Revenue-generating talent who hit quota and stay — not LinkedIn window-shoppers.",
    roles: ["Enterprise AEs & SDRs", "Growth / performance marketers", "Product marketing managers", "RevOps analysts"],
  },
];

export default function GeneralStaffing() {
  return (
    <div data-testid="general-staffing-page">
      <PageHero
        eyebrow="General Staffing"
        title={<>One firm. <span className="italic" style={{ color: "var(--accent-mint)" }}>Every critical hire.</span></>}
        subtitle="Whether you're scaling a tech team or backfilling an operations lead mid-quarter, Ashren handles both — eliminating the overhead of managing 4–6 staffing vendors with inconsistent quality."
        image={HERO_IMG}
        imageAlt="Professionals collaborating in office"
        testId="general-hero"
      />

      <section className="py-16 md:py-24" data-testid="general-practices-list">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 space-y-5">
          {practices.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="card grid md:grid-cols-12 gap-8 items-start" data-testid={`general-practice-${i}`}>
                <div className="md:col-span-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `${p.color}22`, color: p.color }}>
                    <Icon size={24} />
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl leading-tight mb-3">{p.title}</h2>
                  <p className="text-[color:var(--muted)] leading-relaxed">{p.body}</p>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)] mb-4">
                    Roles we place
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {p.roles.map((r) => (
                      <li key={r} className="flex items-start gap-2 text-[color:var(--primary-dark)]">
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: p.color }} />
                        <span className="text-sm">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <CTABand
        title="One contract. One account team. Every critical hire."
        subtitle="Midsize Canadian firms and VC-backed startups prefer us for our speed, compliance depth, and single point of contact across every line."
        primaryLabel="Request a talent brief"
        primaryTo="/for-employers"
        secondaryLabel="Talk to a consultant"
        secondaryTo="/contact"
      />
    </div>
  );
}
